import React, { useState } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';

const DIRECTIONS = [
  { val:'', label:'All Directions' },
  { val:'N', label:'North' },
  { val:'S', label:'South' },
  { val:'E', label:'East' },
  { val:'W', label:'West' },
];
const STAGES = ['','BOX','PART','GANGSAW','GENERAL'];
const WORK_TYPE_LABEL = {
  DRILLING:'Drilling', WIRE_SAW:'Wire Saw', BLASTING:'Blasting',
  OFFSET:'Offset Work', WASTE:'Waste Management', TRANSPORT:'Transport', OTHER:'Other Work'
};

const statusColor = s => s==='COMPLETED'?'#3B6D11':s==='ON_HOLD'?'#854F0B':'#185FA5';
const statusBg    = s => s==='COMPLETED'?'#EAF3DE':s==='ON_HOLD'?'#FAEEDA':'#E6F1FB';

const fmt = d => { try{ return new Date(d).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'}); }catch{ return d; }};
const fmtShort = d => { try{ return new Date(d).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}); }catch{ return d; }};

// Quick date range presets
const getPreset = preset => {
  const today = new Date();
  const iso = d => d.toISOString().split('T')[0];
  if(preset==='today') return { from: iso(today), to: iso(today) };
  if(preset==='yesterday') {
    const y = new Date(today); y.setDate(today.getDate()-1);
    return { from: iso(y), to: iso(y) };
  }
  if(preset==='this_week') {
    const mon = new Date(today);
    mon.setDate(today.getDate() - (today.getDay()===0?6:today.getDay()-1));
    return { from: iso(mon), to: iso(today) };
  }
  if(preset==='this_month') {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: iso(start), to: iso(today) };
  }
  if(preset==='last_month') {
    const start = new Date(today.getFullYear(), today.getMonth()-1, 1);
    const end   = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: iso(start), to: iso(end) };
  }
  return { from: iso(today), to: iso(today) };
};

export default function WorkReportPage() {
  const today = new Date().toISOString().split('T')[0];
  const [filters, setFilters] = useState({ from: today, to: today, direction:'', stage:'' });
  const [viewMode, setViewMode] = useState('summary');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [generated, setGenerated] = useState(false);
  const setF = (k,v) => setFilters(f=>({...f,[k]:v}));

  const applyPreset = preset => {
    const range = getPreset(preset);
    setFilters(f=>({...f,...range}));
  };

  const generate = async () => {
    if(!filters.from||!filters.to){ alert('Please select date range.'); return; }
    setLoading(true);
    try {
      const res = await api.get('/operations/sessions/?page_size=500&ordering=started_date');
      let all = res.data.results||[];
      all = all.filter(s => s.started_date >= filters.from && s.started_date <= filters.to);
      if(filters.direction) all = all.filter(s => s.direction === filters.direction);
      if(filters.stage)     all = all.filter(s => s.production_stage === filters.stage);
      setSessions(all);
      setGenerated(true);
    } finally { setLoading(false); }
  };

  // Build date-wise data
  const byDate = {};
  sessions.forEach(s => {
    if(!byDate[s.started_date]) byDate[s.started_date] = { sessions:[], entries:[] };
    byDate[s.started_date].sessions.push(s);
    try {
      const p = JSON.parse(s.notes||'{}');
      (p.day_entries||[]).forEach(e => {
        if(e.work_type) byDate[s.started_date].entries.push({
          ...e,
          session_number: s.session_number,
          block_reference: s.block_reference,
          direction: s.direction,
          stage: s.production_stage,
          supervisor: s.supervisor_name,
          shift: s.shift,
        });
      });
    } catch {}
  });
  const dates = Object.keys(byDate).sort();

  // Summary stats
  const allEntries   = Object.values(byDate).flatMap(d=>d.entries);
  const totalEntries = allEntries.length;
  const completed    = allEntries.filter(e=>e.status==='COMPLETED').length;
  const inProgress   = allEntries.filter(e=>e.status==='IN_PROGRESS'||!e.status).length;
  const onHold       = allEntries.filter(e=>e.status==='ON_HOLD').length;

  // Work type breakdown
  const wtBreakdown = {};
  allEntries.forEach(e => { wtBreakdown[e.work_type]=(wtBreakdown[e.work_type]||0)+1; });

  const exportCSV = () => {
    const rows = [['Date','Session','Block','Direction','Stage','Supervisor','Shift','Work Type','Status','Hold Reason']];
    dates.forEach(date => {
      byDate[date].entries.forEach(e => {
        rows.push([date,e.session_number,e.block_reference||'—',e.direction||'—',e.stage||'—',e.supervisor||'—',e.shift||'—',WORK_TYPE_LABEL[e.work_type]||e.work_type,e.status||'IN_PROGRESS',e.hold_reason||'']);
      });
    });
    const csv = rows.map(r=>r.map(v=>`"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv],{type:'text/csv'});
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `work_report_${filters.from}_to_${filters.to}.csv`;
    a.click();
  };

  return (
    <Layout title="Work Report" subtitle="Daily · Weekly · Monthly — all in one">
      {/* Filters */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, marginBottom:14 }}>Report Filters</div>

        {/* Quick presets */}
        <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
          {[['today','Today'],['yesterday','Yesterday'],['this_week','This Week'],['this_month','This Month'],['last_month','Last Month']].map(([preset,label])=>(
            <button key={preset} type="button" onClick={()=>applyPreset(preset)}
              style={{ padding:'5px 12px', fontSize:12, borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-input)', color:'var(--text-secondary)', cursor:'pointer', fontWeight:500 }}>
              {label}
            </button>
          ))}
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(110px, 1fr))', gap:12, alignItems:'end' }}>
          <div className="form-group">
            <label className="form-label">From Date *</label>
            <input className="form-input" type="date" value={filters.from} onChange={e=>setF('from',e.target.value)} max={today}/>
          </div>
          <div className="form-group">
            <label className="form-label">To Date *</label>
            <input className="form-input" type="date" value={filters.to} onChange={e=>setF('to',e.target.value)} max={today}/>
          </div>
          <div className="form-group">
            <label className="form-label">Direction</label>
            <select className="form-input" value={filters.direction} onChange={e=>setF('direction',e.target.value)}>
              {DIRECTIONS.map(d=><option key={d.val} value={d.val}>{d.label}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Stage</label>
            <select className="form-input" value={filters.stage} onChange={e=>setF('stage',e.target.value)}>
              {STAGES.map(s=><option key={s} value={s}>{s||'All Stages'}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" style={{ opacity:0 }}>x</label>
            <button className="btn btn-primary" onClick={generate} disabled={loading} style={{ whiteSpace:'nowrap' }}>
              {loading?'Loading...':'Generate'}
            </button>
          </div>
        </div>
      </div>

      {generated && (
        <div>
          {/* Title + Actions */}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <div style={{ minWidth:0 }}>
              <div style={{ fontSize:16, fontWeight:600, color:'var(--text-primary)' }}>
                Work Report — {fmt(filters.from)}{filters.from!==filters.to?` to ${fmt(filters.to)}`:''}
              </div>
              <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>
                {filters.direction?DIRECTIONS.find(d=>d.val===filters.direction)?.label:'All Directions'} · {filters.stage||'All Stages'} · {dates.length} working day{dates.length!==1?'s':''}
              </div>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
              <div style={{ display:'flex', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', overflow:'hidden' }}>
                {[['summary','Summary'],['detailed','Detailed']].map(([val,label])=>(
                  <button key={val} onClick={()=>setViewMode(val)} style={{ padding:'6px 14px', fontSize:12, fontWeight:500, background:viewMode===val?'var(--brand)':'var(--white)', color:viewMode===val?'#fff':'var(--text-secondary)', border:'none', cursor:'pointer' }}>{label}</button>
                ))}
              </div>
              <button className="btn btn-secondary btn-sm" onClick={()=>{
                sessionStorage.setItem('printWorkReport', JSON.stringify({
                  sessions, filters, byDate, dates,
                  totalEntries, completed, inProgress, onHold,
                  wtBreakdown, viewMode
                }));
                setTimeout(()=>{ window.open('/print/work-report', '_blank'); }, 100);
              }}>🖨 Print</button>
              <button className="btn btn-secondary btn-sm" onClick={exportCSV}>📊 Excel</button>
            </div>
          </div>

          {/* KPIs */}
          <div className="kpi-grid" style={{ marginBottom:16 }}>
            <div className="kpi-card brand"><div className="kpi-value">{sessions.length}</div><div className="kpi-label">Sessions</div></div>
            <div className="kpi-card blue"><div className="kpi-value">{totalEntries}</div><div className="kpi-label">Work Entries</div></div>
            <div className="kpi-card green"><div className="kpi-value">{completed}</div><div className="kpi-label">Completed</div></div>
            <div className="kpi-card amber"><div className="kpi-value">{inProgress}</div><div className="kpi-label">In Progress</div></div>
            <div className="kpi-card red"><div className="kpi-value">{onHold}</div><div className="kpi-label">On Hold</div></div>
          </div>

          {/* Work Type Breakdown */}
          {Object.keys(wtBreakdown).length>0&&(
            <div className="card" style={{ marginBottom:16 }}>
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, marginBottom:14 }}>Work Type Breakdown</div>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                {Object.entries(wtBreakdown).map(([wt,cnt])=>(
                  <div key={wt} style={{ padding:'10px 16px', borderRadius:'var(--radius-sm)', background:'var(--bg-primary)', border:'1px solid var(--border)', textAlign:'center', minWidth:100 }}>
                    <div style={{ fontFamily:'DM Mono,monospace', fontSize:22, fontWeight:500, color:'var(--brand)' }}>{cnt}</div>
                    <div style={{ fontSize:12, color:'var(--text-secondary)', marginTop:3 }}>{WORK_TYPE_LABEL[wt]||wt}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* No data */}
          {dates.length===0&&(
            <div className="card" style={{ textAlign:'center', padding:'40px', color:'var(--text-muted)' }}>
              No work sessions found for the selected date range.
            </div>
          )}

          {/* SUMMARY VIEW */}
          {viewMode==='summary' && dates.length>0 && (
            <div className="card">
              <div style={{ fontSize:11, fontWeight:700, letterSpacing:'1.2px', textTransform:'uppercase', color:'var(--brand)', borderBottom:'2px solid var(--border)', paddingBottom:8, marginBottom:16 }}>Day-wise Summary</div>
              <div style={{ overflowX:'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Sessions</th>
                      <th>Entries</th>
                      <th>Work Types</th>
                      <th>Completed</th>
                      <th>In Progress</th>
                      <th>On Hold</th>
                      <th>Blocks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dates.map((date,i)=>{
                      const d = byDate[date];
                      const wtCounts = {};
                      d.entries.forEach(e=>{ wtCounts[e.work_type]=(wtCounts[e.work_type]||0)+1; });
                      return (
                        <tr key={i}>
                          <td style={{ fontWeight:600, color:'var(--brand)', fontFamily:'DM Mono,monospace', whiteSpace:'nowrap' }}>{fmtShort(date)}</td>
                          <td style={{ fontFamily:'DM Mono,monospace', textAlign:'center' }}>{d.sessions.length}</td>
                          <td style={{ fontFamily:'DM Mono,monospace', textAlign:'center' }}>{d.entries.length}</td>
                          <td>
                            {Object.entries(wtCounts).map(([wt,cnt])=>(
                              <span key={wt} style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background:'var(--blue-bg)', color:'var(--blue)', marginRight:4, fontWeight:500 }}>
                                {WORK_TYPE_LABEL[wt]||wt} ×{cnt}
                              </span>
                            ))}
                          </td>
                          <td style={{ fontFamily:'DM Mono,monospace', color:'var(--green)', fontWeight:600, textAlign:'center' }}>{d.entries.filter(e=>e.status==='COMPLETED').length||'—'}</td>
                          <td style={{ fontFamily:'DM Mono,monospace', color:'var(--blue)', textAlign:'center' }}>{d.entries.filter(e=>e.status==='IN_PROGRESS'||!e.status).length||'—'}</td>
                          <td style={{ fontFamily:'DM Mono,monospace', color:'var(--amber)', textAlign:'center' }}>{d.entries.filter(e=>e.status==='ON_HOLD').length||'—'}</td>
                          <td style={{ fontSize:12, color:'var(--text-muted)' }}>
                            {[...new Set(d.sessions.map(s=>s.block_reference).filter(Boolean))].join(', ')||'—'}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Totals */}
                    <tr style={{ borderTop:'2px solid var(--border)', background:'var(--bg-primary)', fontWeight:700 }}>
                      <td>TOTAL</td>
                      <td style={{ fontFamily:'DM Mono,monospace', textAlign:'center', color:'var(--brand)' }}>{sessions.length}</td>
                      <td style={{ fontFamily:'DM Mono,monospace', textAlign:'center', color:'var(--brand)' }}>{totalEntries}</td>
                      <td>—</td>
                      <td style={{ fontFamily:'DM Mono,monospace', color:'var(--green)', fontWeight:700, textAlign:'center' }}>{completed}</td>
                      <td style={{ fontFamily:'DM Mono,monospace', color:'var(--blue)', textAlign:'center' }}>{inProgress}</td>
                      <td style={{ fontFamily:'DM Mono,monospace', color:'var(--amber)', textAlign:'center' }}>{onHold}</td>
                      <td>—</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* DETAILED VIEW */}
          {viewMode==='detailed' && dates.length>0 && (
            <div>
              {dates.map((date,i)=>(
                byDate[date].entries.length>0 && (
                  <div key={i} className="card" style={{ marginBottom:16 }}>
                    <div style={{ background:'#1e3a5f', margin:'-20px -20px 14px -20px', padding:'10px 20px', borderRadius:'var(--radius) var(--radius) 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ color:'#fff', fontWeight:600 }}>{fmt(date)}</span>
                      <span style={{ color:'rgba(255,255,255,0.7)', fontSize:12 }}>{byDate[date].entries.length} entr{byDate[date].entries.length===1?'y':'ies'} · {byDate[date].sessions.length} session{byDate[date].sessions.length===1?'':'s'}</span>
                    </div>
                    <div style={{ overflowX:'auto' }}>
                      <table>
                        <thead>
                          <tr>
                            <th>Session #</th>
                            <th>Block</th>
                            <th>Direction</th>
                            <th>Stage</th>
                            <th>Supervisor</th>
                            <th>Shift</th>
                            <th>Work Type</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {byDate[date].entries.map((e,ei)=>(
                            <tr key={ei}>
                              <td style={{ fontFamily:'DM Mono,monospace', color:'var(--brand)', fontWeight:600, fontSize:12 }}>{e.session_number}</td>
                              <td style={{ fontSize:12 }}>{e.block_reference||'—'}</td>
                              <td style={{ fontSize:12 }}>{e.direction||'—'}</td>
                              <td><span className="badge badge-blue">{e.stage||'—'}</span></td>
                              <td style={{ fontSize:12 }}>{e.supervisor||'—'}</td>
                              <td style={{ fontSize:12 }}>{e.shift||'—'}</td>
                              <td style={{ fontWeight:500 }}>{WORK_TYPE_LABEL[e.work_type]||e.work_type}</td>
                              <td>
                                <span style={{ fontSize:11, padding:'2px 10px', borderRadius:20, fontWeight:500, background:statusBg(e.status||'IN_PROGRESS'), color:statusColor(e.status||'IN_PROGRESS') }}>
                                  {(e.status||'IN_PROGRESS').replace('_',' ')}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )
              ))}
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
